import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./SalesTrendPanel.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

function todayKey() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

export default function SalesTrendPanel() {
  const [shop, setShop] = useState("ALL");
  const [days, setDays] = useState("7");
  const [dateTo, setDateTo] = useState(todayKey());

  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadTrend() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `${API_BASE}/api/erp/sales-trend?shop=${shop}&days=${days}&dateTo=${dateTo}`,
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load sales trend");
      }

      setSummary(data.summary || {});
      setRows(data.rows || []);
    } catch (err) {
      setError(err.message || "Sales trend loading failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, days, dateTo]);

  const maxSales = useMemo(() => {
    return Math.max(...rows.map((row) => Number(row.sales || 0)), 0);
  }, [rows]);

  return (
    <div className="salesTrend">
      <div className="salesTrend__head">
        <div>
          <span>Sales Trend</span>
          <h2>{days} Days Sales Movement</h2>
          <p>
            Daily sales movement for boss view. Shows sales up/down, vouchers,
            qty and average bill.
          </p>
        </div>

        <div className="salesTrend__filters">
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />

          <LocationSelect value={shop} onChange={setShop} mode="shop" includeAll />

          <select value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="7">Last 7 Days</option>
            <option value="14">Last 14 Days</option>
            <option value="30">Last 30 Days</option>
          </select>

          <button onClick={loadTrend} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="salesTrend__error">{error}</div>}

      <div className="salesTrend__cards">
        <div className="salesTrendCard salesTrendCard--main">
          <span>Total Sales</span>
          <strong>{formatMoney(summary.totalSales)} Ks</strong>
          <small>{formatMoney(summary.totalVouchers)} vouchers</small>
        </div>

        <div className="salesTrendCard">
          <span>Total Qty</span>
          <strong>{formatMoney(summary.totalQty)}</strong>
          <small>items sold</small>
        </div>

        <div className="salesTrendCard">
          <span>Average Bill</span>
          <strong>{formatMoney(summary.averageBill)} Ks</strong>
          <small>sales / voucher</small>
        </div>

        <div className="salesTrendCard salesTrendCard--best">
          <span>Best Day</span>
          <strong>{summary.bestDay?.date || "-"}</strong>
          <small>{formatMoney(summary.bestDay?.sales)} Ks</small>
        </div>

        <div className="salesTrendCard salesTrendCard--watch">
          <span>Lowest Day</span>
          <strong>{summary.worstDay?.date || "-"}</strong>
          <small>{formatMoney(summary.worstDay?.sales)} Ks</small>
        </div>
      </div>

      <div className="salesTrendTableWrap">
        <table className="salesTrendTable">
          <thead>
            <tr>
              <th>Date</th>
              <th>Sales Bar</th>
              <th>Sales</th>
              <th>Vouchers</th>
              <th>Qty</th>
              <th>Avg Bill</th>
              <th>Change</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="8" className="salesTrendTable__empty">
                  No sales trend data.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const sales = Number(row.sales || 0);
                const percent = maxSales > 0 ? (sales / maxSales) * 100 : 0;
                const status = row.status || "SAME";

                return (
                  <tr key={row.date}>
                    <td>
                      <b>{row.date}</b>
                    </td>

                    <td>
                      <div className="salesTrendBar">
                        <div
                          className="salesTrendBar__fill"
                          style={{ width: `${Math.max(percent, sales > 0 ? 5 : 0)}%` }}
                        />
                      </div>
                    </td>

                    <td>{formatMoney(row.sales)} Ks</td>
                    <td>{formatMoney(row.vouchers)}</td>
                    <td>{formatMoney(row.qty)}</td>
                    <td>{formatMoney(row.averageBill)} Ks</td>

                    <td>
                      <strong
                        className={
                          Number(row.change || 0) >= 0
                            ? "salesTrendGood"
                            : "salesTrendBad"
                        }
                      >
                        {formatMoney(row.change)} Ks /{" "}
                        {formatPercent(row.changePercent)}
                      </strong>
                    </td>

                    <td>
                      <span
                        className={
                          status === "UP"
                            ? "salesTrendBadge salesTrendBadge--up"
                            : status === "DOWN"
                              ? "salesTrendBadge salesTrendBadge--down"
                              : "salesTrendBadge"
                        }
                      >
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


